import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  useToast,
  Spinner,
  Badge,
  Select,
  HStack,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import EditChannelModal from "../../components/admin/EditChannelModal";
import {
  extractChannelId,
  getChannelData,
} from "../../lib/youtubeChannelUtils";

const PAGE_SIZE = 20;

const GENRE_LIST = [
  "영화/드라마",
  "예능/코미디",
  "스포츠",
  "음식/요리",
  "썰/스토리",
  "개인방송/블로그",
  "IT/경제/재테크",
  "게임",
  "의학/과학/교육",
  "뉴스/정치",
  "뷰티",
  "비영리/사회운동",
  "동물",
  "엔터테인먼트",
  "여행/이벤트",
  "애니메이션",
  "음악/댄스",
  "자동차",
  "지식정보/쇼핑",
  "챌린지",
  "키즈",
  "패션",
];

const AdminChannels = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [approvedFilter, setApprovedFilter] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [channelUrl, setChannelUrl] = useState("");
  const [genre, setGenre] = useState("");
  const [channelType, setChannelType] = useState("A");
  const [selectedUser, setSelectedUser] = useState("");
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserList, setShowUserList] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const toast = useToast();

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("channel").select(`*`, { count: "exact" });

    // 검색 조건
    if (searchQuery) {
      query = query.or(
        `channel_title.ilike.%${searchQuery}%,user.ilike.%${searchQuery}%,channel_id.ilike.%${searchQuery}%`
      );
    }

    // 필터 조건
    if (typeFilter) {
      query = query.eq("type", typeFilter);
    }
    if (statusFilter) {
      if (statusFilter === "Approved") {
        query = query.eq("status", "Approved");
      } else if (statusFilter === "Pending") {
        query = query.eq("status", "Pending");
      } else {
        // 그 외 (null이 아닌 다른 값들)
        query = query.neq("status", "Approved").neq("status", "Pending");
      }
    }
    if (approvedFilter !== "") {
      if (approvedFilter === "true") {
        query = query.eq("approved_yn", true);
      } else {
        // false 또는 null
        query = query.or("approved_yn.eq.false,approved_yn.is.null");
      }
    }

    const { data, error, count } = await query
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (error) {
      toast({
        title: "채널 목록 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setChannels([]);
    } else {
      setChannels(data);
      setTotalPages(Math.ceil(count / PAGE_SIZE));
    }
    setLoading(false);
  }, [page, searchQuery, typeFilter, statusFilter, approvedFilter, toast]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // 사용자 목록 가져오기
  useEffect(() => {
    const fetchUsers = async () => {
      const { data, error } = await supabase
        .from("user")
        .select("email, name")
        .eq("verified", true)
        .order("name");

      if (!error && data) {
        setUsers(data);
      }
    };

    if (isRegisterModalOpen) {
      fetchUsers();
    }
  }, [isRegisterModalOpen]);

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const handleUserSearch = () => {
    const filtered = users.filter(user => 
      user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
    setShowUserList(true);
  };

  const handleUserSelect = (userEmail) => {
    setSelectedUser(userEmail);
    setShowUserList(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchTrigger();
    }
  };

  const handleFilterChange = (filterType, value) => {
    setPage(1);
    if (filterType === "type") {
      setTypeFilter(value);
    } else if (filterType === "status") {
      setStatusFilter(value);
    } else if (filterType === "approved") {
      setApprovedFilter(value);
    }
  };

  const handleApprove = async (channelUrl) => {
    const { error } = await supabase
      .from("channel")
      .update({
        approved_yn: true,
        approved_at: new Date().toISOString(),
      })
      .eq("channel_url", channelUrl);

    if (error) {
      toast({
        title: "승인 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } else {
      toast({
        title: "채널 승인 완료",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchChannels();
    }
  };

  const handleEdit = (channel) => {
    setSelectedChannel(channel);
    setIsEditModalOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ko-KR");
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    return num.toLocaleString();
  };

  const handleRegisterChannel = async () => {
    if (!channelUrl || !genre || !selectedUser) {
      toast({
        title: "모든 항목을 입력해주세요.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setRegistering(true);
    try {
      // 1. 채널 ID 추출
      const channelId = await extractChannelId(channelUrl);
      // 2. 채널 정보 가져오기
      const channelData = await getChannelData(channelId);
      // 3. DB에 저장
      const randomCode = Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase();
      const verifyCode = `${randomCode}#LAEEBLY`;

      const { error } = await supabase.from("channel").insert({
        channel_id: channelId,
        channel_title: channelData.title,
        channel_url: channelUrl,
        thumbnails: channelData.thumbnails,
        genre,
        user: selectedUser,
        status: "Approved",
        approved_yn: false,
        approved_at: null,
        contract: null,
        partner: null,
        permitted_video: null,
        registered_at: new Date().toISOString(),
        verify_code: verifyCode,
        subscriber_count: channelData.subscriberCount,
        views: channelData.viewCount,
        type: channelType,
        created_date: new Date().toISOString(),
        modified_date: new Date().toISOString(),
      });

      if (error) throw error;

      toast({
        title: "채널이 등록되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // 폼 초기화
      setChannelUrl("");
      setGenre("");
      setChannelType("A");
      setSelectedUser("");
      setUserSearchTerm("");
      setShowUserList(false);
      setFilteredUsers([]);
      setIsRegisterModalOpen(false);

      // 목록 갱신
      fetchChannels();
    } catch (e) {
      toast({
        title: e.message || "채널 등록 실패",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setRegistering(false);
    }
  };

  // 채널 삭제 함수
  const handleDeleteChannel = async () => {
    if (!channelToDelete) return;
    try {
      const { error } = await supabase
        .from("channel")
        .delete()
        .eq("id", channelToDelete.id);
      if (error) throw error;
      toast({
        title: "채널이 삭제되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setIsDeleteModalOpen(false);
      setChannelToDelete(null);
      fetchChannels();
    } catch (e) {
      toast({
        title: e.message || "채널 삭제 실패",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">
          채널 관리
        </Heading>
        <Button
          colorScheme="red"
          leftIcon={
            <img
              src="/images/youtube-play.png"
              alt="채널 등록"
              style={{ width: 20, height: 20 }}
            />
          }
          onClick={() => setIsRegisterModalOpen(true)}
        >
          채널 등록
        </Button>
      </Flex>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        {/* 검색 및 필터 */}
        <Flex mb={4} gap={4} flexWrap="wrap">
          <InputGroup maxW="320px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="채널명, 채널아이디, 사용자 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
          <Button onClick={handleSearchTrigger}>검색</Button>

          <Select
            placeholder="타입 선택"
            value={typeFilter}
            onChange={(e) => handleFilterChange("type", e.target.value)}
            maxW="150px"
          >
            <option value="A">브랜드 채널</option>
            <option value="D">일반 채널</option>
            <option value="O">오리지널 채널</option>
            <option value="S">플플 쇼츠 채널</option>
          </Select>

          <Select
            placeholder="상태 선택"
            value={statusFilter}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            maxW="150px"
          >
            <option value="Approved">활성</option>
            <option value="Pending">비활성</option>
            <option value="">그 외</option>
          </Select>

          <Select
            placeholder="승인 상태"
            value={approvedFilter}
            onChange={(e) => handleFilterChange("approved", e.target.value)}
            maxW="150px"
          >
            <option value="true">승인됨</option>
            <option value="false">미승인</option>
          </Select>
        </Flex>

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>채널명</Th>
                  <Th>사용자</Th>
                  <Th>타입</Th>
                  <Th>사용자 인증</Th>
                  <Th>관리자 승인</Th>
                  <Th>구독자</Th>
                  <Th>조회수</Th>
                  <Th>회사</Th>
                  <Th>등록일</Th>
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {channels.map((channel, index) => (
                  <Tr key={index}>
                    <Td>
                      <Tooltip label={channel.channel_url}>
                        <Text
                          fontWeight="medium"
                          cursor="pointer"
                          color="blue.600"
                          _hover={{ textDecoration: "underline" }}
                          onClick={() =>
                            window.open(channel.channel_url, "_blank")
                          }
                        >
                          {channel.channel_title}
                        </Text>
                      </Tooltip>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {channel.genre || "-"}
                      </Text>
                    </Td>
                    <Td>{channel.user}</Td>
                    <Td>
                      <Badge
                        colorScheme={
                          channel.type === "A"
                            ? "blue"
                            : channel.type === "D"
                            ? "green"
                            : channel.type === "O"
                            ? "purple"
                            : channel.type === "S"
                            ? "orange"
                            : "gray"
                        }
                        variant="subtle"
                      >
                        {channel.type === "A"
                          ? "브랜드"
                          : channel.type === "D"
                          ? "일반"
                          : channel.type === "O"
                          ? "오리지널"
                          : channel.type === "S"
                          ? "쇼츠"
                          : "기타"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          channel.status === "Approved"
                            ? "green"
                            : channel.status === "Pending"
                            ? "gray"
                            : "red"
                        }
                        variant="subtle"
                      >
                        {channel.status === "Approved"
                          ? "활성"
                          : channel.status === "Pending"
                          ? "비활성"
                          : "정지"}
                      </Badge>
                    </Td>
                    <Td>
                      {channel.approved_yn ? (
                        <Badge colorScheme="green" variant="subtle">
                          승인됨
                        </Badge>
                      ) : (
                        <Button
                          size="xs"
                          colorScheme="blue"
                          onClick={() => handleApprove(channel.channel_url)}
                        >
                          승인
                        </Button>
                      )}
                    </Td>
                    <Td>{formatNumber(channel.subscriber_count)}</Td>
                    <Td>{formatNumber(channel.views)}</Td>
                    <Td>{channel.company_list || "-"}</Td>
                    <Td>{formatDate(channel.created_date)}</Td>
                    <Td>
                      <Button size="xs" onClick={() => handleEdit(channel)} mr={2}>
                        수정
                      </Button>
                      <Button size="xs" colorScheme="red" onClick={() => { setChannelToDelete(channel); setIsDeleteModalOpen(true); }}>
                        삭제
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex justify="center" align="center" mt={6}>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1}
                size="sm"
              />
              <Text mx={4} fontSize="sm">
                {page} / {totalPages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages}
                size="sm"
              />
            </Flex>
          </>
        )}
      </Box>

      <EditChannelModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        channel={selectedChannel}
        onSave={fetchChannels}
      />

      {/* 채널 등록 모달 */}
      <Modal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW="md">
          <ModalHeader display="flex" alignItems="center" gap={2}>
            <img
              src="/images/youtube-play.png"
              alt="유튜브"
              style={{ width: 32, height: 32 }}
            />
            YouTube 채널 등록
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontWeight="bold" mb={1}>
              사용자 선택
            </Text>
            <Flex gap={2} mb={2}>
              <Input
                placeholder="이름 또는 이메일로 검색"
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUserSearch();
                  }
                }}
              />
              <Button onClick={handleUserSearch} size="md">
                검색
              </Button>
            </Flex>
            {showUserList && (
              <Box 
                borderWidth={1} 
                borderRadius="md" 
                maxH="200px" 
                overflowY="auto" 
                mb={4}
                bg="white"
              >
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <Box
                      key={user.email}
                      p={3}
                      borderBottomWidth={1}
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      onClick={() => handleUserSelect(user.email)}
                    >
                      <Text fontWeight="medium">{user.name}</Text>
                      <Text fontSize="sm" color="gray.600">{user.email}</Text>
                    </Box>
                  ))
                ) : (
                  <Box p={3} textAlign="center" color="gray.500">
                    검색 결과가 없습니다.
                  </Box>
                )}
              </Box>
            )}
            {selectedUser && (
              <Box p={3} bg="blue.50" borderRadius="md" mb={4}>
                <Text fontWeight="medium">선택된 사용자:</Text>
                <Text>{selectedUser}</Text>
              </Box>
            )}

            <Text fontWeight="bold" mb={1}>
              YouTube 채널 등록
            </Text>
            <Input
              placeholder="유튜브 URL 주소 입력"
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              mb={2}
            />
            <Text fontSize="sm" color="gray.500" mb={4}>
              음원 협업을 희망하는 채널의 URL주소를 입력하세요.
            </Text>

            <Text fontWeight="bold" mb={1}>
              채널 장르 선택
            </Text>
            <Select
              placeholder="장르 선택"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              mb={2}
            >
              {GENRE_LIST.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500" mb={4}>
              등록하실 채널의 장르를 선택하세요.
            </Text>

            {/* 채널 유형 선택 */}
            <Text fontWeight="bold" mb={1}>
              채널 유형 선택
            </Text>
            <RadioGroup value={channelType} onChange={setChannelType} mb={2}>
              <Stack direction="row" spacing={6}>
                <Radio value="A">브랜드 채널</Radio>
                <Radio value="D">일반 채널</Radio>
              </Stack>
            </RadioGroup>
            {channelType === "A" && (
              <Box color="gray.600" fontSize="sm" mb={2}>
                영상 권리자 또는 레이블리가 생성한 채널에 크리에이터가 영상
                권리자가 제공하는 콘텐츠를 가지고 쇼츠를 만들어 수익을 만들 수
                있는 채널입니다.
                <br />
                저작권 보호를 받을 수 있으며, 양질의 콘텐츠를 제공받을 수
                있습니다.
              </Box>
            )}
            {channelType === "D" && (
              <Box color="gray.600" fontSize="sm" mb={2}>
                레이블리가 생성한 채널 또는 본인의 채널에 크리에이터가
                레이블리에서 제공되는 콘텐츠를 가지고 쇼츠를 만들어 수익을 만들
                수 있는 채널입니다.
                <br />
                레이블리가 제공한 콘텐츠에 대한 수익만 쉐어합니다.
                <br />
                주의, 채널의 권한을 레이블리에게 공유해야 합니다.
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => setIsRegisterModalOpen(false)}
              mr={3}
              variant="ghost"
            >
              취소
            </Button>
            <Button
              colorScheme="gray"
              onClick={handleRegisterChannel}
              isLoading={registering}
            >
              채널 등록하기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>채널 삭제 확인</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>정말로 이 채널을 삭제하시겠습니까?</Text>
            <Text fontWeight="bold" mt={2}>{channelToDelete?.channel_title}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsDeleteModalOpen(false)}>
              취소
            </Button>
            <Button colorScheme="red" onClick={handleDeleteChannel}>
              삭제
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminChannels;
